import React from "react";
import { Container, Grid, Card, Typography, Box, Stack } from "@mui/material";
import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SectionHeading from "./SectionHeading";
import { colors, fonts, ui } from "../theme";

const stages = [
  {
    n: "01",
    icon: <EmojiObjectsIcon />,
    title: "Awareness & Foundation",
    points: [
      "Building foundational knowledge",
      "Technical workshops & awareness sessions",
      "Transition from passive learning to modern technical literacy",
    ],
    outcome: "Strong Conceptual Clarity",
  },
  {
    n: "02",
    icon: <BuildCircleIcon />,
    title: "Skill Development",
    points: [
      "Deep-dive into modern technologies",
      "Real-world development competencies",
      "Data structures, algorithms & architecture optimization",
    ],
    outcome: "Practical Competence",
  },
  {
    n: "03",
    icon: <WorkOutlineIcon />,
    title: "Internship & Innovation",
    points: [
      "Industry-aligned internship programs",
      "Full-stack & cloud immersion",
      "Hands-on innovation through real-world workflows",
    ],
    outcome: "Industry Exposure",
  },
  {
    n: "04",
    icon: <IntegrationInstructionsIcon />,
    title: "Project Excellence",
    points: [
      "Unique, production-grade projects",
      "Modern architectures & best practices",
      "Deployed on live infrastructure",
    ],
    outcome: "High-Quality Project Portfolio",
  },
  {
    n: "05",
    icon: <MenuBookIcon />,
    title: "Research Awareness",
    points: [
      "Research methodologies & literature review",
      "Technical whitepapers & documentation",
      "Patent ideation & innovation mindset",
    ],
    outcome: "Research & Innovation",
  },
  {
    n: "06",
    icon: <EmojiEventsIcon />,
    title: "Career Readiness",
    points: [
      "Technical portfolio on GitHub",
      "Interview preparation & soft skills",
      "Pathway to top-tier industry placements",
    ],
    outcome: "Career Success",
  },
];

const Framework = () => {
  return (
    <section id="framework-section" className="framework-section">
      <Box sx={{ py: { xs: 5, md: 7 } }}>
        <Container>
          <SectionHeading
            eyebrow="The Crescent Academic Excellence Framework™"
            title="Six Stages — From Awareness to Industry Leadership"
            subtitle="A proprietary methodology that systematically upgrades your institution's academic architecture — moving students through a continuous journey of learning, innovation and excellence."
          />
          <Grid container spacing={3}>
            {stages.map((s) => (
              <Grid item xs={12} sm={6} lg={4} key={s.n}>
                <Card
                  elevation={0}
                  sx={{
                    ...ui.cardHover,
                    height: "100%",
                    p: 3,
                    borderRadius: 3,
                    border: "1px solid #e6edf7",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    sx={{
                      position: "absolute",
                      top: 16,
                      right: 20,
                      fontFamily: fonts.heading,
                      fontWeight: 800,
                      fontSize: "2rem",
                      color: "#eef3fb",
                    }}
                  >
                    {s.n}
                  </Typography>
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: colors.blueTint,
                      color: colors.blueDark,
                      border: "1px solid rgba(56,182,255,0.22)",
                      mb: 2,
                    }}
                  >
                    {s.icon}
                  </Box>
                  <Typography
                    sx={{ fontFamily: fonts.heading, fontWeight: 700, color: colors.navy, mb: 1.5, fontSize: "1.05rem" }}
                  >
                    {s.title}
                  </Typography>
                  <Stack component="ul" spacing={0.8} sx={{ listStyle: "none", pl: 0, m: 0, mb: 2 }}>
                    {s.points.map((pt) => (
                      <Typography
                        component="li"
                        key={pt}
                        sx={{
                          color: colors.textMuted,
                          fontSize: "0.88rem",
                          lineHeight: 1.5,
                          pl: 2,
                          position: "relative",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: "0.55em",
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background: colors.blue,
                          },
                        }}
                      >
                        {pt}
                      </Typography>
                    ))}
                  </Stack>
                  <Box sx={{ mt: "auto", borderTop: "1px dashed #dbe4f0", pt: 1.5 }}>
                    <Typography sx={{ color: colors.blueInk, fontWeight: 700, fontSize: "0.72rem", letterSpacing: "0.06em", mb: 0.5 }}>
                      OUTCOME
                    </Typography>
                    <Typography sx={{ color: colors.navy, fontSize: "0.9rem", fontWeight: 600, lineHeight: 1.5 }}>
                      {s.outcome}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </section>
  );
};

export default Framework;
